import React, { useState, useCallback, useEffect } from "react";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import useThemeColor from "../../hooks/useThemeColor.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import swal from "sweetalert";
import moment from "moment";
import "./UpdateCompany.css";

/** Maps API CompDevices / tbl_Devices row (camelCase JSON) to stable display fields */
const mapDeviceFromApi = (raw) => {
  if (!raw || typeof raw !== "object") return {};
  return {
    deviceID: raw.deviceID ?? raw.DeviceID ?? "",
    deviceName: raw.deviceName ?? raw.name ?? raw.Name ?? "",
    /** App column (e.g. POS, KIOS) — shown as "Type" */
    app: raw.app ?? raw.App ?? "",
    os: raw.os ?? raw.OS ?? "",
    model: raw.model ?? raw.Model ?? "",
    deviceCode: raw.device_Code ?? raw.deviceCode ?? "",
    deviceStatus: (raw.deviceStatus ?? raw.DeviceStatus ?? "").trim(),
    registrationDate: raw.registrationDate ?? raw.RegistrationDate ?? null,
    appVersion: raw.appVersion ?? raw.AppVersion ?? "",
  };
};

const dash = (value) => {
  if (value === null || value === undefined) return "—";
  const s = String(value).trim();
  return s.length ? s : "—";
};

const iconBtnStyle = (color) => ({
  width: "32px",
  height: "32px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "6px",
  border: `1px solid ${color}`,
  color,
  padding: 0,
  background: "transparent",
  transition: "all 0.2s ease",
});

const CompanyDevices = ({ CompID }) => {
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const [loading, setLoading] = useState(false);
  const [deviceList, setDeviceList] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [deviceData, setDeviceData] = useState({
    DeviceID: "",
    DeviceName: "",
    DeviceType: "",
    DeviceModel: "",
    SerialNumber: "",
    MACAddress: "",
    IPAddress: "",
    Location: "",
    IsActive: true,
    LastSeen: "",
    CompID: parseInt(CompID),
    AssignedUser: "",
    Notes: "",
  });

  const deviceTypes = [
    { value: "iPad", label: "iPad" },
    { value: "Android Tablet", label: "Android Tablet" },
    { value: "POS Terminal", label: "POS Terminal" },
    { value: "Kitchen Display", label: "Kitchen Display" },
    { value: "Customer Display", label: "Customer Display" },
    { value: "Mobile Phone", label: "Mobile Phone" },
    { value: "Other", label: "Other" },
  ];

  // Fetch company devices
  const fetchCompanyDevices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_DEVICES, {
        params: { CompID: CompID },
      });

      const payload = response.data?.data ?? response.data;
      const list = Array.isArray(payload) ? payload : [];
      setDeviceList(list);
    } catch (error) {
      showErrorToast("Failed to load company devices");
      console.error("Error fetching devices:", error);
    } finally {
      setLoading(false);
    }
  }, [CompID]);

  const handleInputChange = useCallback((e, field) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setDeviceData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleAddDevice = async () => {
    const { DeviceName, DeviceType, DeviceModel } = deviceData;

    if (!DeviceName.trim()) {
      showErrorToast("Please provide a device name");
      return;
    }

    if (!DeviceType.trim()) {
      showErrorToast("Please select a device type");
      return;
    }

    if (!DeviceModel.trim()) {
      showErrorToast("Please provide a device model");
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.ADD_COMPANY_DEVICE, deviceData);

      if (response.data.status) {
        showSuccessToast("Device added successfully");
        setShowAddForm(false);
        resetForm();
        fetchCompanyDevices();
      } else {
        showErrorToast(response.data.message || "Error adding device");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Error adding device");
      console.error("Error adding device:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDevice = async () => {
    const { DeviceName, DeviceType, DeviceModel } = deviceData;

    if (!DeviceName.trim()) {
      showErrorToast("Please provide a device name");
      return;
    }

    if (!DeviceType.trim()) {
      showErrorToast("Please select a device type");
      return;
    }

    if (!DeviceModel.trim()) {
      showErrorToast("Please provide a device model");
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.put(APIs.UPDATE_COMPANY_DEVICE, deviceData);

      if (response.data.status) {
        showSuccessToast("Device updated successfully");
        setEditingDevice(null);
        resetForm();
        fetchCompanyDevices();
      } else {
        showErrorToast(response.data.message || "Error updating device");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Error updating device");
      console.error("Error updating device:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDevice = async (deviceID) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: "Are you sure you want to delete this device?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) {
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_COMPANY_DEVICE, {
        params: { DeviceID: deviceID, CompID: CompID },
      });

      if (response.data.status) {
        showSuccessToast("Device deleted successfully");
        fetchCompanyDevices();
      } else {
        showErrorToast(response.data.message || "Error deleting device");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Error deleting device");
      console.error("Error deleting device:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditDevice = (device) => {
    const m = mapDeviceFromApi(device);
    setEditingDevice(device);
    setDeviceData({
      DeviceID: m.deviceID,
      DeviceName: m.deviceName,
      DeviceType: m.app,
      DeviceModel: m.model,
      SerialNumber: m.deviceCode,
      MACAddress: "",
      IPAddress: "",
      Location: "",
      IsActive: true,
      LastSeen: m.registrationDate ? String(m.registrationDate) : "",
      CompID: parseInt(CompID),
      AssignedUser: "",
      Notes: m.appVersion ? `App version: ${m.appVersion}` : "",
    });
    setShowAddForm(true);
  };

  const resetForm = () => {
    setDeviceData({
      DeviceID: "",
      DeviceName: "",
      DeviceType: "",
      DeviceModel: "",
      SerialNumber: "",
      MACAddress: "",
      IPAddress: "",
      Location: "",
      IsActive: true,
      LastSeen: "",
      CompID: parseInt(CompID),
      AssignedUser: "",
      Notes: "",
    });
    setEditingDevice(null);
    setShowAddForm(false);
  };

  /** Uses server DeviceStatus when present; otherwise infers from registration date */
  const getDeviceStatusBadge = (device) => {
    const m = mapDeviceFromApi(device);
    const st = m.deviceStatus;
    if (st) {
      const u = st.toLowerCase();
      if (u.includes("online") || u === "active" || u === "1") {
        return { status: st, pillClass: "uc-badge-pill text-success", style: { background: "rgba(25, 135, 84, 0.12)" } };
      }
      if (u.includes("offline") || u.includes("inactive") || u === "0") {
        return { status: st, pillClass: "uc-badge-pill text-secondary", style: { background: "rgba(108, 117, 125, 0.12)" } };
      }
      return { status: st, pillClass: "uc-badge-pill text-info", style: { background: "rgba(13, 202, 240, 0.15)" } };
    }
    if (m.registrationDate) {
      const reg = moment(m.registrationDate);
      if (reg.isValid()) {
        const diffMin = moment().diff(reg, "minutes");
        if (diffMin < 24 * 60) {
          return {
            status: "Registered (recent)",
            pillClass: "uc-badge-pill text-warning",
            style: { background: "rgba(255, 193, 7, 0.18)", color: isDarkMode ? "#ffc107" : "#856404" },
          };
        }
        return {
          status: "Registered",
          pillClass: "uc-badge-pill text-secondary",
          style: { background: "rgba(108, 117, 125, 0.12)" },
        };
      }
    }
    return {
      status: "Unknown",
      pillClass: "uc-badge-pill text-secondary",
      style: { background: "rgba(108, 117, 125, 0.12)" },
    };
  };

  const formatLastRegistered = (device) => {
    const m = mapDeviceFromApi(device);
    if (!m.registrationDate) return "—";
    const d = moment(m.registrationDate);
    return d.isValid() ? d.format("MMM DD, YYYY HH:mm") : "—";
  };

  useEffect(() => {
    fetchCompanyDevices();
  }, [fetchCompanyDevices]);

  return (
    <div>
      {loading && <Preloader />}

      <section className="uc-section uc-section--nested">
        <div className="uc-section__header">
          <h5>Company Devices</h5>
          <p>Registered POS and related devices for this company</p>
        </div>
        <div className="uc-section__body">
          {deviceList.length === 0 ? (
            <div className="uc-empty">
              <i className="fa fa-mobile-alt"></i>
              No devices found for this company.
            </div>
          ) : (
            <div className="uc-table-wrap">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Device Name</th>
                    <th>Type</th>
                    <th>Model</th>
                    <th>Serial/Code</th>
                    <th>Status</th>
                    <th>Last registered</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deviceList.map((device, index) => {
                    const mapped = mapDeviceFromApi(device);
                    const deviceStatus = getDeviceStatusBadge(device);
                    const typeLabel = dash(mapped.app || mapped.os);
                    return (
                      <tr key={mapped.deviceID || index}>
                        <td>{dash(mapped.deviceName)}</td>
                        <td>{typeLabel}</td>
                        <td>{dash(mapped.model)}</td>
                        <td>{dash(mapped.deviceCode)}</td>
                        <td>
                          <span className={deviceStatus.pillClass} style={deviceStatus.style}>
                            {deviceStatus.status}
                          </span>
                        </td>
                        <td>{formatLastRegistered(device)}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <button
                              type="button"
                              className="btn btn-sm uc-icon-btn"
                              title="Edit device"
                              style={iconBtnStyle(themeColor)}
                              onClick={() => handleEditDevice(device)}
                            >
                              <i className="fa fa-edit" style={{ fontSize: "12px" }}></i>
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm uc-icon-btn"
                              title="Delete device"
                              style={iconBtnStyle("#dc3545")}
                              onClick={() => handleDeleteDevice(mapped.deviceID)}
                            >
                              <i className="fa fa-trash" style={{ fontSize: "12px" }}></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <CustomModal
        title={editingDevice ? "Edit Device" : "Add New Device"}
        isOpen={showAddForm}
        onClose={resetForm}
        onSubmit={editingDevice ? handleUpdateDevice : handleAddDevice}
        showButton={true}
        showCancelButton={true}
        showSubmitButton={true}
        submitButtonText={loading ? "Processing..." : editingDevice ? "Update Device" : "Add Device"}
        submitButtonDisabled={loading}
        isCentered={true}
        modalSize="modal-lg"
        maxHeight="80vh"
        minHeight="auto"
      >
        <div className="row mb-3">
          <div className="col-6">
            <FormInput
              label="Device Name"
              placeHolder="Enter Device Name"
              type="text"
              value={deviceData.DeviceName}
              onchange={(e) => handleInputChange(e, "DeviceName")}
            />
          </div>
          <div className="col-6">
            <label className="uc-label">Device Type</label>
            <select
              className="form-control"
              value={deviceData.DeviceType}
              onChange={(e) => handleInputChange(e, "DeviceType")}
            >
              <option value="">Select Device Type</option>
              {deviceTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-6">
            <FormInput
              label="Device Model"
              placeHolder="Enter Device Model"
              type="text"
              value={deviceData.DeviceModel}
              onchange={(e) => handleInputChange(e, "DeviceModel")}
            />
          </div>
          <div className="col-6">
            <FormInput
              label="Serial Number"
              placeHolder="Enter Serial Number"
              type="text"
              value={deviceData.SerialNumber}
              onchange={(e) => handleInputChange(e, "SerialNumber")}
            />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-6">
            <FormInput
              label="MAC Address"
              placeHolder="Enter MAC Address"
              type="text"
              value={deviceData.MACAddress}
              onchange={(e) => handleInputChange(e, "MACAddress")}
            />
          </div>
          <div className="col-6">
            <FormInput
              label="IP Address"
              placeHolder="Enter IP Address"
              type="text"
              value={deviceData.IPAddress}
              onchange={(e) => handleInputChange(e, "IPAddress")}
            />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-6">
            <FormInput
              label="Location"
              placeHolder="Enter Device Location"
              type="text"
              value={deviceData.Location}
              onchange={(e) => handleInputChange(e, "Location")}
            />
          </div>
          <div className="col-6">
            <FormInput
              label="Assigned User"
              placeHolder="Enter Assigned User"
              type="text"
              value={deviceData.AssignedUser}
              onchange={(e) => handleInputChange(e, "AssignedUser")}
            />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-6">
            <ToggleSwitch
              isChecked={deviceData.IsActive}
              onToggle={(e) => handleInputChange(e, "IsActive")}
              label="Active Status"
              name="IsActive"
            />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-12">
            <label className="uc-label">Notes</label>
            <textarea
              className="form-control"
              placeholder="Enter device notes"
              value={deviceData.Notes}
              onChange={(e) => handleInputChange(e, "Notes")}
              rows="3"
            />
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default CompanyDevices;
